import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, Mail, MapPin, Home, DollarSign, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const LEAD_TYPE_LABELS = {
  contact_form: 'Contact Form', buy_form: 'Buyer Inquiry', sell_form: 'Seller Inquiry',
  tour_request: 'Tour Request', booking: 'Booking',
  property_gate: 'Property Gate', home_valuation: 'Home Valuation',
};

const METHOD_BADGE = {
  manual: { label: 'Manual', bg: '#EEF2FF', fg: '#4338CA' },
  rule: { label: 'Auto-Rule', bg: '#DCFCE7', fg: '#166534' },
  round_robin: { label: 'Round-Robin', bg: '#FEF3C7', fg: '#92400E' },
};

const MyLeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/agent/my-leads');
        setLeads(res.data.leads || []);
      } catch (e) {
        toast.error('Failed to load your leads');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-gray-500 p-6">Loading your leads…</div>;

  if (leads.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500" data-testid="my-leads-empty">
          <div className="text-4xl mb-2">🎯</div>
          <div className="font-semibold text-gray-700 mb-1">No leads assigned yet</div>
          <div className="text-sm">Leads assigned to you via auto-routing or by an admin will appear here.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="my-leads">
      <div className="text-sm text-gray-600 mb-2">{leads.length} lead{leads.length === 1 ? '' : 's'} assigned to you</div>
      {leads.map((item, i) => {
        const l = item.lead;
        const badge = METHOD_BADGE[item.method] || METHOD_BADGE.round_robin;
        return (
          <Card key={item.assignment_id || i} data-testid={`my-lead-${i}`}>
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-lg text-[#434345]">{l.name || l.email}</h3>
                    <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded" style={{ backgroundColor: badge.bg, color: badge.fg }}>{badge.label}</span>
                  </div>
                  <div className="text-xs text-gray-500">{LEAD_TYPE_LABELS[item.lead_type] || item.lead_type} · assigned {new Date(item.assigned_at).toLocaleString()}</div>
                </div>
                <div className="flex flex-col sm:items-end gap-1 text-sm">
                  {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-[#BCA480] hover:underline"><Mail className="h-3.5 w-3.5" />{l.email}</a>}
                  {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-[#BCA480] hover:underline"><Phone className="h-3.5 w-3.5" />{l.phone}</a>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
                {(l.location_preference || l.property_address || l.address) && (
                  <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" /><span>{l.location_preference || l.property_address || l.address}</span></div>
                )}
                {l.price_range && <div className="flex items-start gap-2"><DollarSign className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" /><span>{l.price_range}</span></div>}
                {l.property_type && <div className="flex items-start gap-2"><Home className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" /><span>{l.property_type}</span></div>}
                {(l.timeline || l.timeframe) && <div className="flex items-start gap-2"><Clock className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" /><span>{l.timeline || l.timeframe}</span></div>}
              </div>
              {(l.message || l.additional_info || l.notes) && (
                <div className="mt-3 p-3 bg-[#f4efe6] rounded text-sm text-gray-700 border-l-2 border-[#BCA480]">
                  {l.message || l.additional_info || l.notes}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default MyLeadsTab;
